import { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { urlSync } from '../utils/urlSync';
import { redirectPreservation } from '../../../../utils/redirectPreservation';

const ANALYTICS_REDIRECT_KEY = 'analytics_filters';

const ANALYTICS_ROUTES = [
  '/team-insights/sessions',
  '/team-insights/utilization',
  '/team-insights/wellness',
  '/team-insights/system-performance',
] as const;

type AnalyticsRoute = (typeof ANALYTICS_ROUTES)[number];

function isValidAnalyticsRoute(path: string): path is AnalyticsRoute {
  return ANALYTICS_ROUTES.includes(path as AnalyticsRoute);
}

export function useUrlPreservation() {
  const navigate = useNavigate();

  const preserveCurrentUrl = useCallback(() => {
    if (urlSync.shouldPreserveFilters()) {
      const currentUrl = window.location.href;
      redirectPreservation.preserve(ANALYTICS_REDIRECT_KEY, currentUrl);
    }
  }, []);

  const handlePostLoginRedirect = useCallback(() => {
    const preservedUrl = redirectPreservation.restore(ANALYTICS_REDIRECT_KEY);

    if (preservedUrl) {
      try {
        const url = new URL(preservedUrl);

        // Validate the path is a known analytics route
        if (isValidAnalyticsRoute(url.pathname)) {
          navigate({
            to: url.pathname,
            search: url.search,
          });
          return true;
        }
      } catch (error) {
        console.warn('Failed to parse preserved URL:', error);
      }
    }

    return false;
  }, [navigate]);

  const hasPreservedUrl = useCallback(() => {
    return redirectPreservation.hasRedirect(ANALYTICS_REDIRECT_KEY);
  }, []);

  return {
    preserveCurrentUrl,
    handlePostLoginRedirect,
    hasPreservedUrl,
  };
}
